import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, ListMusic, Maximize2, MicVocal, Pause, Play, Repeat, Repeat1, Shuffle, SkipBack, SkipForward, Volume1, Volume2, VolumeX } from 'lucide-react';
import { currentTrack, usePlayer } from '../player/store';
import { useFavoriteToggle, useFavorites } from '../api/hooks';
import { fmtTime, artistLine } from '../lib/format';
import { Cover } from './Cover';

export function Seekbar({ compact }: { compact?: boolean }) {
  const t = usePlayer((s) => s.currentTime);
  const d = usePlayer((s) => s.duration || currentTrack(s)?.duration || 0);
  const seek = usePlayer((s) => s.seek);
  const [drag, setDrag] = useState<number | null>(null);
  const shown = drag ?? t;
  const pct = d ? Math.min(100, (shown / d) * 100) : 0;
  return (
    <div className={`seek ${compact ? 'seek--compact' : ''}`}>
      {!compact && <span className="seek__time">{fmtTime(shown)}</span>}
      <input
        type="range"
        className="range"
        min={0}
        max={d || 1}
        step={0.5}
        value={shown}
        style={{ ['--pct' as string]: `${pct}%` }}
        aria-label="Position de lecture"
        aria-valuetext={`${fmtTime(shown)} sur ${fmtTime(d)}`}
        onChange={(e) => setDrag(Number(e.target.value))}
        onPointerUp={() => {
          if (drag !== null) seek(drag);
          setDrag(null);
        }}
        onKeyUp={() => {
          if (drag !== null) seek(drag);
          setDrag(null);
        }}
      />
      {!compact && <span className="seek__time">{fmtTime(d)}</span>}
    </div>
  );
}

export function Transport({ big }: { big?: boolean }) {
  const playing = usePlayer((s) => s.playing);
  const buffering = usePlayer((s) => s.buffering);
  const { toggle, next, prev, toggleShuffle, cycleRepeat } = usePlayer.getState();
  const shuffle = usePlayer((s) => s.shuffle);
  const repeat = usePlayer((s) => s.repeat);
  const has = usePlayer((s) => s.queue.length > 0);
  const sz = big ? 26 : 20;
  return (
    <div className={`transport ${big ? 'transport--big' : ''}`}>
      <button className={`icon-btn ${shuffle ? 'is-on' : ''}`} onClick={toggleShuffle} aria-pressed={shuffle} aria-label="Lecture aléatoire" title="Lecture aléatoire">
        <Shuffle size={big ? 22 : 18} />
      </button>
      <button className="icon-btn" onClick={prev} disabled={!has} aria-label="Titre précédent" title="Précédent (P)">
        <SkipBack size={sz} fill="currentColor" />
      </button>
      <button className={`btn-play ${big ? 'btn-play--big' : ''} ${buffering && playing ? 'is-buffering' : ''}`} onClick={toggle} disabled={!has} aria-label={playing ? 'Pause' : 'Lecture'} title="Lecture/pause (Espace)">
        {playing ? <Pause size={sz} fill="currentColor" /> : <Play size={sz} fill="currentColor" />}
      </button>
      <button className="icon-btn" onClick={() => next()} disabled={!has} aria-label="Titre suivant" title="Suivant (N)">
        <SkipForward size={sz} fill="currentColor" />
      </button>
      <button
        className={`icon-btn ${repeat !== 'off' ? 'is-on' : ''}`}
        onClick={cycleRepeat}
        aria-label={repeat === 'one' ? 'Répéter le titre' : repeat === 'all' ? 'Répéter la file' : 'Répétition désactivée'}
        title="Répétition"
      >
        {repeat === 'one' ? <Repeat1 size={big ? 22 : 18} /> : <Repeat size={big ? 22 : 18} />}
      </button>
    </div>
  );
}

export function Volume() {
  const volume = usePlayer((s) => s.volume);
  const muted = usePlayer((s) => s.muted);
  const { setVolume, toggleMute } = usePlayer.getState();
  const Icon = muted || volume === 0 ? VolumeX : volume < 0.5 ? Volume1 : Volume2;
  return (
    <div className="volume">
      <button className="icon-btn" onClick={toggleMute} aria-label={muted ? 'Réactiver le son' : 'Couper le son'} title="Muet (M)">
        <Icon size={18} />
      </button>
      <input
        type="range"
        className="range range--small"
        min={0}
        max={1}
        step={0.01}
        value={muted ? 0 : volume}
        style={{ ['--pct' as string]: `${(muted ? 0 : volume) * 100}%` }}
        onChange={(e) => setVolume(Number(e.target.value))}
        aria-label="Volume"
      />
    </div>
  );
}

export function FavButton({ id, size = 18 }: { id: string; size?: number }) {
  const favs = useFavorites();
  const fav = useFavoriteToggle();
  const on = !!favs.data?.tracks.some((t) => t.id === id);
  return (
    <button className={`icon-btn fav ${on ? 'is-on' : ''}`} onClick={() => fav.mutate({ kind: 'track', id, value: !on })} aria-pressed={on} aria-label={on ? 'Retirer des favoris' : 'Ajouter aux favoris'}>
      <Heart size={size} fill={on ? 'currentColor' : 'none'} />
    </button>
  );
}

export function PlayerBar() {
  const t = usePlayer(currentTrack);
  const open = usePlayer((s) => s.openNowPlaying);
  const nowOpen = usePlayer((s) => s.nowPlayingOpen);
  const tab = usePlayer((s) => s.panelTab);
  const touchY = useRef<number | null>(null);

  if (!t) {
    return (
      <footer className="playerbar playerbar--empty">
        <span>Choisissez un album, un compositeur ou une playlist pour commencer l’écoute.</span>
      </footer>
    );
  }
  const toggleTab = (which: 'queue' | 'lyrics') => (nowOpen && tab === which ? open(false) : open(true, which));
  return (
    <footer
      className="playerbar"
      onTouchStart={(e) => (touchY.current = e.touches[0].clientY)}
      onTouchEnd={(e) => {
        if (touchY.current !== null && touchY.current - e.changedTouches[0].clientY > 40) open(true);
        touchY.current = null;
      }}
    >
      <div className="playerbar__now">
        <button className="playerbar__art" onClick={() => open(!nowOpen)} aria-label="Afficher le lecteur plein écran">
          <Cover art={t.cover} name={t.album ?? t.title} size={128} />
        </button>
        <div className="playerbar__meta" onClick={() => window.matchMedia('(max-width: 820px)').matches && open(true)}>
          <span className="playerbar__title">{t.title}</span>
          <span className="playerbar__artist">
            {t.artists[0]?.id ? <Link to={`/artist/${t.artists[0].id}`}>{artistLine(t)}</Link> : artistLine(t)}
          </span>
        </div>
        <FavButton id={t.id} />
      </div>
      <div className="playerbar__center">
        <Transport />
        <Seekbar />
      </div>
      <div className="playerbar__right">
        <button className={`icon-btn ${nowOpen && tab === 'lyrics' ? 'is-on' : ''}`} onClick={() => toggleTab('lyrics')} aria-label="Paroles" title="Paroles (L)">
          <MicVocal size={18} />
        </button>
        <button className={`icon-btn ${nowOpen && tab === 'queue' ? 'is-on' : ''}`} onClick={() => toggleTab('queue')} aria-label="File d’attente" title="File d’attente (Q)">
          <ListMusic size={18} />
        </button>
        <Volume />
        <button className="icon-btn" onClick={() => open(!nowOpen)} aria-label="Lecteur plein écran" title="Plein écran">
          <Maximize2 size={17} />
        </button>
      </div>
      <div className="playerbar__mobile">
        <MobileToggle />
      </div>
      <Seekbar compact />
    </footer>
  );
}

function MobileToggle() {
  const playing = usePlayer((s) => s.playing);
  const toggle = usePlayer((s) => s.toggle);
  return (
    <button className="icon-btn icon-btn--big" onClick={toggle} aria-label={playing ? 'Pause' : 'Lecture'}>
      {playing ? <Pause size={24} fill="currentColor" /> : <Play size={24} fill="currentColor" />}
    </button>
  );
}
